import { EMPTY_ARRAY_LENGTH, NumberUtils } from '@modeso/aumico-lib-core-fe';
import {
    AccountMappingTemplateStatus,
    IAccountMappingDataAutoFilled,
    IAccountMappingFormInput,
    isGroup,
} from '@modeso/aumico-lib-templates-fe';
import {
    FinancialStatementTypes,
    IAccount,
    IAccountMappingSubFLI,
    IAccountMappingTemplate,
    IAccountMappingTemplateGroup,
    IAccountMappingTemplateGroupItemFLI,
    IAccountMappingTemplateGroupItemSubtotal,
    IFinStatements,
    IReclassesAccounts,
    ItemType,
    SortedMappingItem,
} from '@modeso/types__aumico-lib-annualreport-be';
import { IGroupEntityDTO } from '@modeso/types__aumico-lib-templates-be';
import { cloneDeep } from 'lodash-es';

export class FinancialStatementAccountMappingFormHelper {
    static isAllFinStatementsHaveMapping(finStatements: IFinStatements[]): boolean {
        let hasCurrentMapping: boolean;
        // check if all finstatements have mappings to be used
        finStatements.forEach((finStatement) => {
            if (finStatement.templateAndAccountsMapping) {
                hasCurrentMapping = true;
            } else {
                hasCurrentMapping = false;
            }
        });
        return hasCurrentMapping;
    }

    static prepareAccountMappingInput(
        autofillFinStatements: IFinStatements[],
        defaultTemplatesGroups: Map<string, IGroupEntityDTO>,
        balanceAccounts: IAccount[] | undefined,
        incomeAccounts: IAccount[] | undefined,
        isPreviousDataTakenFromPreviousAnnualReport: boolean,
        reportLanguage: string,
        isGetAnnualReportForUploadAndMappingAutoFill: boolean,
    ): IAccountMappingFormInput {
        const accountMappingInput: IAccountMappingFormInput = {} as IAccountMappingFormInput;
        const status: AccountMappingTemplateStatus = isPreviousDataTakenFromPreviousAnnualReport
            ? AccountMappingTemplateStatus.PREVIOUS_AUTOMATIC_FILL
            : isGetAnnualReportForUploadAndMappingAutoFill
            ? AccountMappingTemplateStatus.COPY_FROM_OTHER_REPORT
            : AccountMappingTemplateStatus.AUTOFILLED;

        autofillFinStatements.forEach((finStatement) => {
            if (finStatement && finStatement.type === FinancialStatementTypes.BALANCE) {
                accountMappingInput.balance = {
                    template:
                        finStatement.templateAndAccountsMapping.template != null
                            ? this.getUpdatedTemplate(
                                  finStatement.templateAndAccountsMapping.template,
                                  defaultTemplatesGroups,
                                  reportLanguage,
                                  isGetAnnualReportForUploadAndMappingAutoFill,
                              )
                            : null,
                    status,
                    accounts: balanceAccounts != null ? balanceAccounts : [],
                    balanceSwitchAccounts:
                        finStatement.templateAndAccountsMapping.balanceSwitchAccounts != null
                            ? finStatement.templateAndAccountsMapping.balanceSwitchAccounts
                            : [],
                };
                accountMappingInput.balance = this.changeClosingToOpeningOfReclassesInAccounts(
                    accountMappingInput.balance as IAccountMappingDataAutoFilled,
                );
            } else if (finStatement && finStatement.type === FinancialStatementTypes.INCOME) {
                accountMappingInput.income = {
                    template:
                        finStatement.templateAndAccountsMapping.template != null
                            ? this.getUpdatedTemplate(
                                  finStatement.templateAndAccountsMapping.template,
                                  defaultTemplatesGroups,
                                  reportLanguage,
                                  isGetAnnualReportForUploadAndMappingAutoFill,
                              )
                            : null,
                    status,
                    accounts: incomeAccounts != null ? incomeAccounts : [],
                    balanceSwitchAccounts:
                        finStatement.templateAndAccountsMapping.balanceSwitchAccounts != null
                            ? finStatement.templateAndAccountsMapping.balanceSwitchAccounts
                            : [],
                };
                accountMappingInput.income = this.changeClosingToOpeningOfReclassesInAccounts(
                    accountMappingInput.income as IAccountMappingDataAutoFilled,
                );
            }
        });
        return accountMappingInput;
    }
    static changeClosingToOpeningOfReclassesInAccounts(
        balance: IAccountMappingDataAutoFilled,
    ): IAccountMappingDataAutoFilled {
        // I am cloning here to avoid having this transformation run twice on the same items
        const newBalance = cloneDeep(balance);
        this.autoFillReclassesInAccountsArray(newBalance.accounts);
        if (newBalance.template != null) {
            this.autoFillReclassesInGroup(newBalance.template.group);
        }
        return newBalance;
    }
    static autoFillReclassesInGroup(group: IAccountMappingTemplateGroup) {
        for (const subGroup of group.groups || []) {
            this.autoFillReclassesInGroup(subGroup);
        }
        for (const item of group.items) {
            if (item.type === ItemType.FLI) {
                this.autofillReclassesInFLI(item);
            }
        }
    }
    static autofillReclassesInFLI(
        inputFLi:
            | IAccountMappingTemplateGroupItemSubtotal
            | IAccountMappingTemplateGroupItemFLI
            | IAccountMappingSubFLI,
    ): void {
        if ((inputFLi as IAccountMappingTemplateGroupItemSubtotal).type === ItemType.SUB_TOTAL) {
            return;
        }
        const fli = inputFLi as IAccountMappingTemplateGroupItemFLI | IAccountMappingSubFLI;

        for (const item of fli.accounts || []) {
            if (item.type === ItemType.SUB_FLI || item.type === ItemType.OF_THAT_ITEM) {
                this.autofillReclassesInFLI(item);
            } else if (item.type !== ItemType.EMPTY && item.type !== ItemType.HEADER) {
                this.autoFillReclassesInAccount(item as IAccount);
            }
        }
        for (const adjustment of fli.reclassesAccounts || []) {
            this.autoFillReclassesInAccount(adjustment);
        }
    }

    private static autoFillReclassesInAccountsArray(accounts: (IAccount | IReclassesAccounts)[]): void {
        for (const account of accounts || []) {
            this.autoFillReclassesInAccount(account);
        }
    }

    /**
     * Advances one fiscal year for a single account/reclass row: the current-year
     * reclass/adjustment becomes the previous-year value and the current year is
     * cleared (`openingReClass = closingReClass; closingReClass = 0`, same for
     * adjustments). Public so the autofill reclass-account carry
     * (copyTemplateReclassesAccounts) can apply the SAME shift to synthetic
     * `reclassesAccounts` rows that it applies to regular accounts.
     */
    public static autoFillReclassesInAccount(account: IAccount | IReclassesAccounts): void {
        let openingReClass: number | undefined;
        if (account.closingReClass != null && NumberUtils.isFinite(account.closingReClass)) {
            openingReClass = NumberUtils.roundNumber(account.closingReClass);
        }

        let openingAdjustment: number | undefined;
        if (account.closingAdjustment != null && NumberUtils.isFinite(account.closingAdjustment)) {
            openingAdjustment = NumberUtils.roundNumber(account.closingAdjustment);
        }

        account.openingReClass = openingReClass;
        account.openingAdjustment = openingAdjustment;
        account.closingReClass = 0;
        account.closingAdjustment = 0;
    }

    static isAccountsMappingDefined(
        activeFinStatements: IFinStatements[],
        balanceIndex: number,
        incomeIndex: number,
    ): boolean {
        const indicesExist = balanceIndex !== -1 && incomeIndex !== -1;
        if (!indicesExist) {
            return false;
        }
        if (
            activeFinStatements[balanceIndex] == null ||
            activeFinStatements[balanceIndex].templateAndAccountsMapping == null ||
            activeFinStatements[balanceIndex].templateAndAccountsMapping.accounts == null
        ) {
            return false;
        }
        if (
            activeFinStatements[incomeIndex] == null ||
            activeFinStatements[incomeIndex].templateAndAccountsMapping == null ||
            activeFinStatements[incomeIndex].templateAndAccountsMapping.accounts == null
        ) {
            return false;
        }
        return true;
    }

    static isTemplateMappingDefined(
        finStatements: IFinStatements[],
        balanceIndex: number,
        incomeIndex: number,
    ): boolean {
        const indicesExist = balanceIndex !== -1 && incomeIndex !== -1;
        if (!indicesExist) {
            return false;
        }
        if (
            finStatements[balanceIndex] == null ||
            finStatements[balanceIndex].templateAndAccountsMapping == null ||
            finStatements[balanceIndex].templateAndAccountsMapping.template == null
        ) {
            return false;
        }
        if (
            finStatements[incomeIndex] == null ||
            finStatements[incomeIndex].templateAndAccountsMapping == null ||
            finStatements[incomeIndex].templateAndAccountsMapping.template == null
        ) {
            return false;
        }
        return true;
    }

    static extractActiveAnnualReportAccounts(
        activeFinStatements: IFinStatements[],
        balanceIndex: number,
        incomeIndex: number,
    ): {
        extractedBalanceAccounts: IAccount[];
        extractedIncomeAccounts: IAccount[];
    } | null {
        if (
            !FinancialStatementAccountMappingFormHelper.isAccountsMappingDefined(
                activeFinStatements,
                balanceIndex,
                incomeIndex,
            )
        ) {
            return null;
        }
        const balanceAccounts = activeFinStatements[balanceIndex].templateAndAccountsMapping.accounts;
        const incomeAccounts = activeFinStatements[incomeIndex].templateAndAccountsMapping.accounts;
        return { extractedBalanceAccounts: balanceAccounts, extractedIncomeAccounts: incomeAccounts };
    }

    static getIndexForFinancialStatementSourceFromActiveAnnualReport(
        activeFinStatements: IFinStatements[],
        financialStatementType: FinancialStatementTypes,
    ): number {
        return activeFinStatements.findIndex(
            (financialStatement) => financialStatement != null && financialStatement.type === financialStatementType,
        );
    }

    static getDuplicateAccountNumber(accounts: IAccount[]): Set<string> {
        const accountNumbers = accounts.map((account) => account.accountNumber);
        const allDuplicateAccountNumber = accountNumbers.filter(
            (accountNumber, index) => accountNumbers.indexOf(accountNumber) !== index,
        );
        return new Set<string>(allDuplicateAccountNumber);
    }

    private static getUpdatedTemplate(
        previousMappedTemplate: IAccountMappingTemplate,
        defaultTemplatesGroups: Map<string, IGroupEntityDTO>,
        currentLanguage: string,
        isGetAnnualReportForUploadAndMappingAutoFill: boolean,
    ): IAccountMappingTemplate {
        const updatedTemplateGroup = this.updateGroup(
            previousMappedTemplate.group,
            defaultTemplatesGroups,
            currentLanguage,
            isGetAnnualReportForUploadAndMappingAutoFill,
        );

        return {
            name: previousMappedTemplate.name,
            group: updatedTemplateGroup,
            type: previousMappedTemplate.type,
            accountBasis: previousMappedTemplate.accountBasis,
            legalForm: previousMappedTemplate.legalForm,
            profitOfTheYear: previousMappedTemplate.profitOfTheYear,
            equityGroupName: previousMappedTemplate.equityGroupName,
            uncoveredShortfallAssetsFli: previousMappedTemplate.uncoveredShortfallAssetsFli,
            uncoveredShortfallLiabilitiesFli: previousMappedTemplate.uncoveredShortfallLiabilitiesFli,
            annualSurplusFli: previousMappedTemplate.annualSurplusFli,
        };
    }

    private static updateGroup(
        groupToUpdate: IAccountMappingTemplateGroup,
        defaultTemplatesGroups: Map<string, IGroupEntityDTO>,
        reportLocale: string,
        isGetAnnualReportForUploadAndMappingAutoFill: boolean,
    ): IAccountMappingTemplateGroup {
        const matchingGroup = defaultTemplatesGroups.get(groupToUpdate.name);
        if (matchingGroup != null) {
            groupToUpdate.title = matchingGroup.title[reportLocale];
            groupToUpdate.totalName = matchingGroup.totalName[reportLocale];
        }

        if (isGetAnnualReportForUploadAndMappingAutoFill) {
            groupToUpdate.sortedItems.forEach((sortedItem: SortedMappingItem) => {
                if (sortedItem.type === ItemType.FLI) {
                    sortedItem.reclassesAccounts = [];
                    sortedItem.accounts.forEach((account) => {
                        if (account.type === ItemType.OF_THAT_ITEM || account.type === ItemType.SUB_FLI) {
                            account.reclassesAccounts = [];
                        }
                    });
                }
            });
        }

        const groups = (groupToUpdate.sortedItems as SortedMappingItem[]).filter((sortedItem) => isGroup(sortedItem));
        for (let group of groups) {
            group = this.updateGroup(
                group as IAccountMappingTemplateGroup,
                defaultTemplatesGroups,
                reportLocale,
                isGetAnnualReportForUploadAndMappingAutoFill,
            );
        }

        return groupToUpdate;
    }

    public static checkIfAnyGroupHasNoFlis(financialStatements: IFinStatements[]): boolean {
        for (const financialStatement of financialStatements) {
            const isGroupEmpty = FinancialStatementAccountMappingFormHelper.isGroupEmpty(
                financialStatement.templateAndAccountsMapping.template.group,
            );
            if (isGroupEmpty) {
                return isGroupEmpty;
            }
        }

        return false;
    }

    private static isGroupEmpty(group: IAccountMappingTemplateGroup): boolean {
        // Check if the current group is empty meaning that it has no flis and no groups
        const flisInsideGroup = group.items.filter((items) => items.type === ItemType.FLI);
        if (
            !group.isHeader &&
            !group.isEmpty &&
            flisInsideGroup.length === EMPTY_ARRAY_LENGTH &&
            group.groups.length === EMPTY_ARRAY_LENGTH
        ) {
            return true;
        }

        for (const subGroup of group.groups) {
            const subGroupIsEmpty = FinancialStatementAccountMappingFormHelper.isGroupEmpty(subGroup);
            if (subGroupIsEmpty) {
                return subGroupIsEmpty;
            }
        }

        return false;
    }
}
